/**
 * Работа с датами.
 *
 * Все календарные даты (дата выплаты, дата начала контракта) хранятся и
 * считаются как «день без времени» в UTC-полночь. Это избавляет от классического
 * бага, когда выплата 1-го числа превращается в 31-е из-за часового пояса.
 */

export type ISODate = string; // 'YYYY-MM-DD'

const DAY_MS = 24 * 60 * 60 * 1000;

export function isValidISODate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = parseISODate(value);
  return !Number.isNaN(date.getTime()) && toISODate(date) === value;
}

/** 'YYYY-MM-DD' → Date в UTC-полночь. */
export function parseISODate(value: ISODate): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

/** Date → 'YYYY-MM-DD' по UTC-компонентам. */
export function toISODate(date: Date): ISODate {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Сегодняшняя дата в указанном часовом поясе (по умолчанию — часовой пояс машины). */
export function todayISO(timeZone?: string): ISODate {
  const now = new Date();
  if (!timeZone) return toISODate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
    return parts; // en-CA даёт ровно YYYY-MM-DD
  } catch {
    return toISODate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  }
}

export function addDays(value: ISODate, days: number): ISODate {
  return toISODate(new Date(parseISODate(value).getTime() + days * DAY_MS));
}

export function addWeeks(value: ISODate, weeks: number): ISODate {
  return addDays(value, weeks * 7);
}

export function daysInMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
}

/**
 * Прибавляет месяцы с «прилипанием» к концу месяца.
 * 31.01 + 1 месяц = 28.02 (или 29.02 в високосный год).
 */
export function addMonths(value: ISODate, months: number): ISODate {
  const date = parseISODate(value);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const targetIndex = month + months;
  const targetYear = year + Math.floor(targetIndex / 12);
  const targetMonth = ((targetIndex % 12) + 12) % 12;
  const maxDay = daysInMonth(targetYear, targetMonth + 1);

  return toISODate(new Date(Date.UTC(targetYear, targetMonth, Math.min(day, maxDay))));
}

/**
 * Собирает дату из года/месяца и «дня выплаты».
 * dayOfMonth = -1 означает последний день месяца.
 * Если в месяце нет такого числа (31 февраля), берётся последний день.
 */
export function dateFromMonthDay(year: number, month1to12: number, dayOfMonth: number): ISODate {
  const maxDay = daysInMonth(year, month1to12);
  const day = dayOfMonth === -1 ? maxDay : Math.min(Math.max(1, dayOfMonth), maxDay);
  return toISODate(new Date(Date.UTC(year, month1to12 - 1, day)));
}

export function startOfMonth(value: ISODate): ISODate {
  const date = parseISODate(value);
  return toISODate(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
}

export function endOfMonth(value: ISODate): ISODate {
  const date = parseISODate(value);
  return toISODate(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), daysInMonth(date.getUTCFullYear(), date.getUTCMonth() + 1))),
  );
}

/** Количество полных дней между датами: diffDays('2026-09-01', '2026-09-04') === 3. */
export function diffDays(from: ISODate, to: ISODate): number {
  return Math.round((parseISODate(to).getTime() - parseISODate(from).getTime()) / DAY_MS);
}

export function compareISO(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minISO(a: ISODate, b: ISODate): ISODate {
  return a <= b ? a : b;
}

export function maxISO(a: ISODate, b: ISODate): ISODate {
  return a >= b ? a : b;
}

/** Ключ периода для зарплаты: '2026-08'. */
export function monthKey(value: ISODate): string {
  return value.slice(0, 7);
}

export function monthKeyToRange(key: string): { from: ISODate; to: ISODate } {
  const [y, m] = key.split('-').map(Number);
  const year = y ?? 1970;
  const month = m ?? 1;
  return {
    from: dateFromMonthDay(year, month, 1),
    to: dateFromMonthDay(year, month, -1),
  };
}

/** Сдвиг ключа месяца: shiftMonthKey('2026-01', -1) === '2025-12'. */
export function shiftMonthKey(key: string, months: number): string {
  const { from } = monthKeyToRange(key);
  return monthKey(addMonths(from, months));
}

/** День недели: 0 — воскресенье, 1 — понедельник … 6 — суббота. */
export function weekdayOf(value: ISODate): number {
  return parseISODate(value).getUTCDay();
}

/** Возраст в полных годах на указанную дату. */
export function ageOn(birthDate: ISODate, on: ISODate = todayISO()): number {
  const birth = parseISODate(birthDate);
  const target = parseISODate(on);
  let age = target.getUTCFullYear() - birth.getUTCFullYear();
  const monthDiff = target.getUTCMonth() - birth.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && target.getUTCDate() < birth.getUTCDate())) {
    age -= 1;
  }
  return Math.max(0, age);
}

/** Date (с временем) → ISODate, безопасно для значений из БД. */
export function dateOnly(value: Date | string): ISODate {
  if (typeof value === 'string') {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  return toISODate(value);
}
